import { DashboardLayout } from "@/components/DashboardLayout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  BookOpen,
  FileText,
  Video,
  MessageCircle,
  Search,
  ExternalLink,
  Mail,
} from "lucide-react";
import { useState } from "react";

const HELP_CATEGORIES = [
  {
    title: "Getting Started",
    icon: BookOpen,
    articles: [
      { title: "Creating Your First Resume", url: "#" },
      { title: "Understanding ATS Optimization", url: "#" },
      { title: "Using the Application Tracker", url: "#" },
      { title: "Setting Up Your Profile", url: "#" },
    ],
  },
  {
    title: "Resume & Cover Letters",
    icon: FileText,
    articles: [
      { title: "Resume Writing Best Practices", url: "#" },
      { title: "Tailoring Your Resume for Jobs", url: "#" },
      { title: "Cover Letter Templates", url: "#" },
      { title: "Exporting to PDF & Word", url: "#" },
    ],
  },
  {
    title: "Job Search",
    icon: Search,
    articles: [
      { title: "Finding the Right Jobs", url: "#" },
      { title: "Using Job Filters Effectively", url: "#" },
      { title: "Saving and Organizing Jobs", url: "#" },
      { title: "Smart Apply Feature", url: "#" },
    ],
  },
  {
    title: "Account & Billing",
    icon: Mail,
    articles: [
      { title: "Understanding Credits System", url: "#" },
      { title: "Upgrading Your Plan", url: "#" },
      { title: "Managing Your Subscription", url: "#" },
      { title: "Billing & Payment FAQs", url: "#" },
    ],
  },
];

const VIDEO_TUTORIALS = [
  {
    title: "Quick Start Guide",
    duration: "5 min",
    thumbnail: "/placeholder.svg",
    url: "#",
  },
  {
    title: "Creating ATS-Optimized Resumes",
    duration: "8 min",
    thumbnail: "/placeholder.svg",
    url: "#",
  },
  {
    title: "Using Smart Apply",
    duration: "6 min",
    thumbnail: "/placeholder.svg",
    url: "#",
  },
];

export default function Help() {
  const [searchQuery, setSearchQuery] = useState("");

  return (
    <DashboardLayout>
      <div className="container max-w-6xl mx-auto py-8 px-4">
        {/* Header */}
        <div className="mb-8 text-center">
          <h1 className="text-4xl font-bold mb-2">Help Center</h1>
          <p className="text-lg text-muted-foreground">
            Everything you need to succeed in your job search
          </p>
        </div>

        {/* Search */}
        <div className="mb-12">
          <div className="relative max-w-2xl mx-auto">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-5 w-5" />
            <Input
              placeholder="Search for help articles..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 h-12 text-lg"
            />
          </div>
        </div>

        {/* Quick Actions */}
        <div className="grid md:grid-cols-3 gap-4 mb-12">
          <Card className="hover:shadow-lg transition-shadow cursor-pointer">
            <CardContent className="pt-6 text-center">
              <MessageCircle className="h-12 w-12 mx-auto mb-3 text-primary" />
              <h3 className="font-semibold mb-2">Live Chat Support</h3>
              <p className="text-sm text-muted-foreground mb-4">
                Get instant help from our team
              </p>
              <Button size="sm" className="w-full">
                Start Chat
              </Button>
            </CardContent>
          </Card>

          <Card className="hover:shadow-lg transition-shadow cursor-pointer">
            <CardContent className="pt-6 text-center">
              <Mail className="h-12 w-12 mx-auto mb-3 text-primary" />
              <h3 className="font-semibold mb-2">Email Support</h3>
              <p className="text-sm text-muted-foreground mb-4">
                We'll respond within 24 hours
              </p>
              <Button size="sm" variant="outline" className="w-full">
                <a href="mailto:support@loopbaan.ai">Send Email</a>
              </Button>
            </CardContent>
          </Card>

          <Card className="hover:shadow-lg transition-shadow cursor-pointer">
            <CardContent className="pt-6 text-center">
              <BookOpen className="h-12 w-12 mx-auto mb-3 text-primary" />
              <h3 className="font-semibold mb-2">Documentation</h3>
              <p className="text-sm text-muted-foreground mb-4">
                Detailed guides and tutorials
              </p>
              <Button size="sm" variant="outline" className="w-full">
                View Docs
              </Button>
            </CardContent>
          </Card>
        </div>

        {/* Video Tutorials */}
        <div className="mb-12">
          <h2 className="text-2xl font-bold mb-6 flex items-center gap-2">
            <Video className="h-6 w-6" />
            Video Tutorials
          </h2>
          <div className="grid md:grid-cols-3 gap-6">
            {VIDEO_TUTORIALS.map((video) => (
              <Card key={video.title} className="overflow-hidden hover:shadow-lg transition-shadow">
                <div className="aspect-video bg-muted relative">
                  <div className="absolute inset-0 flex items-center justify-center">
                    <Video className="h-12 w-12 text-muted-foreground" />
                  </div>
                </div>
                <CardContent className="pt-4">
                  <h3 className="font-semibold mb-1">{video.title}</h3>
                  <p className="text-sm text-muted-foreground mb-3">{video.duration}</p>
                  <Button size="sm" variant="outline" className="w-full">
                    Watch Now
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>

        {/* Help Categories */}
        <div className="grid md:grid-cols-2 gap-6">
          {HELP_CATEGORIES.map((category) => {
            const Icon = category.icon;
            return (
              <Card key={category.title}>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Icon className="h-5 w-5" />
                    {category.title}
                  </CardTitle>
                  <CardDescription>
                    Common questions and guides
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-2">
                    {category.articles.map((article) => (
                      <li key={article.title}>
                        <a
                          href={article.url}
                          className="flex items-center justify-between p-2 rounded hover:bg-accent transition-colors"
                        >
                          <span className="text-sm">{article.title}</span>
                          <ExternalLink className="h-4 w-4 text-muted-foreground" />
                        </a>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Contact Section */}
        <Card className="mt-12 bg-primary/5 border-primary/20">
          <CardContent className="pt-6 text-center">
            <h3 className="text-xl font-semibold mb-2">Still Need Help?</h3>
            <p className="text-muted-foreground mb-4">
              Our support team is here to help you succeed
            </p>
            <div className="flex gap-3 justify-center">
              <Button>
                <MessageCircle className="h-4 w-4 mr-2" />
                Chat with Us
              </Button>
              <Button variant="outline">
                <Mail className="h-4 w-4 mr-2" />
                Send Email
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}
